import { useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Plus } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import {
  formatCOP,
  computeVariantPrice,
  getSize,
  getFrame,
} from "../data/products";

export const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const [isAdding, setIsAdding] = useState(false);

  const unitPrice = computeVariantPrice(product.basePrice, product.defaultSize);
  const size = getSize(product.defaultSize);
  const frame = getFrame("negro");

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAdding(true);
    addItem({
      productId: product.id,
      title: product.title,
      image: product.image,
      sizeId: size.id,
      sizeLabel: size.label,
      frameId: frame.id,
      frameLabel: frame.label,
      unitPrice,
    });
    toast.success("Cuadro añadido a tu carrito", {
      description: `${product.title} · ${size.label}`,
    });
    setTimeout(() => setIsAdding(false), 600);
  };

  return (
    <Link
      to={`/producto/${product.id}`}
      data-testid={`product-card-${product.id}`}
      className="group flex flex-col bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
    >
      {/* Image */}
      <div className="relative aspect-[4/5] overflow-hidden bg-slate-100">
        <img
          src={product.image}
          alt={product.title}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
        />
        <span
          data-testid={`product-category-${product.id}`}
          className="absolute top-3 left-3 inline-flex items-center bg-white/85 backdrop-blur-sm text-slate-800 text-[11px] font-medium uppercase tracking-wider px-2.5 py-1 rounded-full"
        >
          {product.category}
        </span>
        {product.bestseller && (
          <span
            data-testid={`product-bestseller-${product.id}`}
            className="absolute top-3 right-3 inline-flex items-center gap-1 bg-[#E85A3C] text-white text-[11px] font-medium px-2.5 py-1 rounded-full shadow-md shadow-[#E85A3C]/30"
          >
            <Flame className="h-3 w-3" strokeWidth={2} />
            Más vendido
          </span>
        )}
      </div>

      {/* Info */}
      <div className="p-5 flex flex-col flex-grow">
        <h3
          data-testid={`product-title-${product.id}`}
          className="font-serif text-lg md:text-xl text-slate-900 leading-snug line-clamp-1"
        >
          {product.title}
        </h3>
        <p
          data-testid={`product-size-${product.id}`}
          className="text-sm text-slate-500 mt-1"
        >
          Desde {size.label}
        </p>

        <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
          <span
            data-testid={`product-price-${product.id}`}
            className="text-lg font-semibold text-slate-900 tabular-nums"
          >
            {formatCOP(unitPrice)}
          </span>
          <button
            type="button"
            onClick={handleAdd}
            disabled={isAdding}
            data-testid={`add-to-cart-button-${product.id}`}
            aria-label={`Añadir ${product.title} al carrito`}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-slate-900 text-white text-xs font-medium hover:bg-[#E85A3C] transition-colors duration-300 active:scale-95 disabled:opacity-60"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
            Añadir
          </button>
        </div>
      </div>
    </Link>
  );
};
